<template>
  <section class="section" id="product-change-logs-section">
    <div class="container">
      <b-loading :is-full-page="true" :active="isLoading" :can-cancel="false" />

      <product-change-logs-title
        @search="searchProductChangeLogs"
        @download="downloadReport"
        @clear-filters="clearFilters"
        @update-store="updateStore"
        @update-dates="updateDates"
        @update-field="updateField"
        @update-search="updateSearch"
      />

      <product-change-logs-table :product-change-logs="productChangeLogs" />

      <b-pagination
        v-if="productChangeLogsQty > perPage"
        :total="productChangeLogsQty"
        v-model="current"
        order="is-centered"
        :per-page="perPage"
        aria-next-label="Próxima página"
        aria-previous-label="Página anterior"
        aria-page-label="Página"
        aria-current-label="Página atual"
        @change="changePage"
      ></b-pagination>
    </div>
  </section>
</template>

<script>
import { common } from "../../mixins/common";
import productChangeLogsTitle from "./ProductChangeLogsTitle";
import productChangeLogsTable from "./ProductChangeLogsTable";

export default {
  mixins: [common],

  components: { productChangeLogsTitle, productChangeLogsTable },

  data() {
    return {
      isLoading: false,
      storeId: "",
      dates: [],
      field: "",
      search: "",
      current: 1,
      perPage: 100,
      appliedFilters: {},
      productChangeLogs: [],
      productChangeLogsQty: 0
    };
  },

  methods: {
    // O período é escolhido em datas locais, então o dia final precisa ir até
    // o último instante para não cortar as alterações mais recentes.
    buildFilters() {
      const filters = {};

      if (this.storeId) filters.storeId = this.storeId;
      if (this.field) filters.field = this.field;
      if (this.search) filters.search = this.search;

      if (Array.isArray(this.dates) && this.dates.length === 2) {
        filters.beginDate = this.moment(this.dates[0])
          .startOf("day")
          .toISOString();
        filters.endDate = this.moment(this.dates[1])
          .endOf("day")
          .toISOString();
      }

      return filters;
    },

    getProductChangeLogs(skip) {
      if (isNaN(skip)) {
        skip = (this.current - 1) * this.perPage;
      }

      this.isLoading = true;

      const filters = this.buildFilters();

      return this.$store
        .dispatch("getProductChangeLogs", {
          ...filters,
          skip,
          limit: this.perPage
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.appliedFilters = filters;
            this.productChangeLogs = res.data.payload.productChangeLogs;
            this.productChangeLogsQty = res.data.payload.productChangeLogsQty;
          }
        })
        .catch(() => this.alertError({ message: "UNEXPECTED_ERROR" }))
        .finally(() => {
          this.isLoading = false;
        });
    },

    // Trocar um filtro muda o conjunto de resultados, então a busca sempre
    // recomeça da primeira página.
    searchProductChangeLogs() {
      this.current = 1;
      return this.getProductChangeLogs(0);
    },

    updateStore(storeId) {
      this.storeId = storeId;
      return this.searchProductChangeLogs();
    },

    updateField(field) {
      this.field = field;
      return this.searchProductChangeLogs();
    },

    // Buscar a cada tecla geraria uma requisição por caractere, então o texto
    // só consulta ao confirmar com Enter. Esvaziar o campo tira o filtro na
    // hora, senão a tabela ficaria presa no último termo pesquisado.
    updateSearch(search) {
      const hadSearch = Boolean(this.search);
      this.search = search;

      if (hadSearch && !search) return this.searchProductChangeLogs();
    },

    // O calendário de período só produz um recorte útil com as duas pontas
    // escolhidas, então a recarga espera o intervalo fechar.
    updateDates(dates) {
      this.dates = dates || [];

      if (this.dates.length !== 2 || !this.dates[0] || !this.dates[1]) return;

      return this.searchProductChangeLogs();
    },

    clearFilters() {
      this.storeId = "";
      this.dates = [];
      this.field = "";
      this.search = "";

      return this.searchProductChangeLogs();
    },

    changePage(page) {
      return this.getProductChangeLogs((page - 1) * this.perPage);
    },

    // O relatório segue os filtros da última busca, e não o que está digitado
    // no campo de busca, para sair igual à tabela que está na tela.
    downloadReport() {
      if (!this.productChangeLogsQty) {
        this.alertError({ message: "NO_PRODUCT_LOGS_TO_EXPORT" });
        return Promise.resolve();
      }

      this.isLoading = true;

      return this.$store
        .dispatch("downloadProductChangeLogsReport", this.appliedFilters)
        .catch(() => this.alertError({ message: "UNEXPECTED_ERROR" }))
        .finally(() => {
          this.isLoading = false;
        });
    }
  },

  mounted() {
    this.getProductChangeLogs(0);
  }
};
</script>

<style scoped>
#product-change-logs-section {
  margin-top: 3rem;
  min-height: 94vh;
  padding: 2rem 1rem;
  background-color: hsl(0, 0%, 96%);
}
</style>
